import React, { useCallback, useEffect, useRef, useState } from "react";

import type { FlowEngine } from "../../services/flows";
import type { Step } from "../../services/flows/types";
import { useFileSizeFormatter } from "../../utils/formatters";

interface ImageInputStepType extends Step {
  type: "imageInput";
  prompt: string;
  required?: boolean;
  storeAs?: string; // Variable name to store the image
  source?: "camera" | "gallery"; // Specify camera or gallery source
  // Image-specific validation stored in step properties
  fileTypes?: string[]; // e.g., ['image/jpeg', 'image/png']
  maxSize?: number; // in bytes
}

interface ImageInputStepProps {
  step: ImageInputStepType;
  engine: FlowEngine;
  onMessage: (message: {
    from: string;
    text: string;
    imageUrl?: string;
  }) => void;
}

const ImageInputStep: React.FC<ImageInputStepProps> = ({
  step,
  engine,
  onMessage,
}) => {
  const [isWaitingForInput, setIsWaitingForInput] = useState(false);
  const [, setValidationError] = useState<string | null>(null);
  const hasShownPromptRef = useRef(false);
  const formatFileSize = useFileSizeFormatter();

  const requestInput = useCallback(() => {
    setIsWaitingForInput(true);
    setValidationError(null);

    // Emit event to signal input is needed
    engine.emit("inputRequested", {
      type: "image",
      config: {
        required: step.required !== false, // Default to true
        validation: step.validation,
        source: step.source, // Pass through the source if specified
      },
      step: step,
    });
  }, [engine, step]);

  // Show prompt on mount
  useEffect(() => {
    if (!hasShownPromptRef.current && step.prompt) {
      onMessage({ from: "bot", text: __(step.prompt) });
      hasShownPromptRef.current = true;
      requestInput();
    }
  }, [step.prompt, onMessage, requestInput]);

  const validateImage = useCallback(
    (file: File): string | null => {
      const validation = step.validation;
      if (!validation) return null;

      // Check file type
      if (validation.fileTypes && validation.fileTypes.length > 0) {
        if (!validation.fileTypes.includes(file.type)) {
          const types = validation.fileTypes
            .map(t => t.replace("image/", ""))
            .join(", ");
          return `${__('File type not supported. Please use')}: ${types}`;
        }
      }

      // Check file size
      if (validation.maxSize && file.size > validation.maxSize) {
        return `${__('File too large. Maximum size')}: ${formatFileSize(validation.maxSize)}`;
      }

      return null;
    },
    [step.validation, formatFileSize]
  );

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error(__('Failed to convert file to base64')));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }, []);

  // Listen for input completion
  useEffect(() => {
    const handleInputComplete = async (event: any) => {
      if (!isWaitingForInput || event.type !== "image") return;

      const file = event.file;

      // Handle skip for optional fields
      if (!file && step.required === false) {
        setIsWaitingForInput(false);
        engine.goToNext(step.next || "");
        return;
      }

      // Validate required fields
      if (!file && step.required !== false) {
        setValidationError(__('Please provide an image'));
        onMessage({ from: "bot", text: __('Please provide an image') });
        requestInput(); // Request again
        return;
      }

      // Validate image
      const error = validateImage(file);
      if (error) {
        setValidationError(error);
        onMessage({ from: "bot", text: error });
        requestInput(); // Request again
        return;
      }

      try {
        // Convert to base64
        const base64 = await fileToBase64(file);

        // Success - update variable
        setIsWaitingForInput(false);
        engine.updateBuiltInVariable("lastImage", base64);

        // Store in specified variable if storeAs is provided
        if (step.storeAs) {
          engine.setVariable(step.storeAs, base64);
        }

        // Add user message with image
        const imageUrl = URL.createObjectURL(file);
        onMessage({
          from: "user",
          text: __('Image uploaded'),
          imageUrl: imageUrl,
        });

        // Navigate to next step
        if (step.next) {
          engine.goToNext(step.next);
        }
      } catch (err) {
        setValidationError(__('Failed to process image'));
        onMessage({
          from: "bot",
          text: __('Failed to process image. Please try again.'),
        });
        requestInput(); // Request again
      }
    };

    engine.on("inputComplete", handleInputComplete);

    return () => {
      engine.off("inputComplete", handleInputComplete);
    };
  }, [
    isWaitingForInput,
    step.required,
    step.next,
    step.storeAs,
    engine,
    onMessage,
    requestInput,
    validateImage,
    fileToBase64,
  ]);

  // Component is purely orchestration - no visual elements
  return null;
};

export default ImageInputStep;
